'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning';
import { cn } from '@/lib/utils';
type ReasoningBlockProps = {
  className?: string;
  text: string;
  defaultOpen: boolean;
  autoCollapse: boolean;
  isStreaming: boolean;
};
const TITLE_MAX_LENGTH = 80;
function extractReasoningTitle(text: string): string | undefined {
  const firstLine = text.split('\n').find(line => line.trim().length > 0);
  if (!firstLine) {
    return undefined;
  }
  const cleaned = firstLine.replace(/^#+\s*/, '').replace(/\*\*/g, '').replace(/\*/g, '').trim();
  if (cleaned.length === 0) {
    return undefined;
  }
  if (cleaned.length > TITLE_MAX_LENGTH) {
    return `${cleaned.slice(0, TITLE_MAX_LENGTH - 3)}...`;
  }
  return cleaned;
}
const REASONING_TEXT_CLASS = '[&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs [&_li]:text-xs [&_ol]:text-xs [&_p]:text-xs [&_table]:text-xs [&_td]:text-xs [&_th]:text-xs [&_ul]:text-xs';
const REASONING_STICK_TO_BOTTOM_THRESHOLD = 24;
export function ReasoningBlock(t0) {
  const $ = _c(34);
  const {
    className,
    text,
    defaultOpen,
    autoCollapse,
    isStreaming
  } = t0;
  const resolvedDefaultOpen = autoCollapse ? false : defaultOpen;
  let t1;
  if ($[0] !== isStreaming || $[1] !== resolvedDefaultOpen) {
    t1 = () => resolvedDefaultOpen || isStreaming;
    $[0] = isStreaming;
    $[1] = resolvedDefaultOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [open, setOpen] = useState(t1);
  const [previousAutoCollapse, setPreviousAutoCollapse] = useState(autoCollapse);
  let t2;
  if ($[3] !== text) {
    t2 = text.trim();
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasReasoningText = t2.length > 0;
  let t3;
  if ($[5] !== text) {
    t3 = extractReasoningTitle(text);
    $[5] = text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const title = t3;
  const containerRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  if (previousAutoCollapse !== autoCollapse) {
    setPreviousAutoCollapse(autoCollapse);
    if (autoCollapse) {
      setOpen(false);
    }
  }
  let t4;
  if ($[7] !== open) {
    t4 = () => {
      const reasoningContent = containerRef.current?.querySelector("[data-reasoning-content=\"true\"]");
      if (!reasoningContent || !open) {
        return;
      }
      const updateStickiness = () => {
        const distanceFromBottom = reasoningContent.scrollHeight - reasoningContent.scrollTop - reasoningContent.clientHeight;
        shouldStickToBottomRef.current = distanceFromBottom <= REASONING_STICK_TO_BOTTOM_THRESHOLD;
      };
      updateStickiness();
      reasoningContent.addEventListener("scroll", updateStickiness);
      return () => {
        reasoningContent.removeEventListener("scroll", updateStickiness);
      };
    };
    $[7] = open;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hasReasoningText || $[10] !== open) {
    t5 = [hasReasoningText, open];
    $[9] = hasReasoningText;
    $[10] = open;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== open) {
    t6 = () => {
      if (!open || !shouldStickToBottomRef.current) {
        return;
      }
      const reasoningContent_0 = containerRef.current?.querySelector("[data-reasoning-content=\"true\"]");
      if (!reasoningContent_0) {
        return;
      }
      const animationFrame = requestAnimationFrame(() => {
        reasoningContent_0.scrollTop = reasoningContent_0.scrollHeight;
      });
      return () => cancelAnimationFrame(animationFrame);
    };
    $[12] = open;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isStreaming || $[15] !== open || $[16] !== text) {
    t7 = [text, isStreaming, open];
    $[14] = isStreaming;
    $[15] = open;
    $[16] = text;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t10;
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0
    };
    t9 = {
      opacity: 1
    };
    t10 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
  } else {
    t10 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t11;
  if ($[21] !== className) {
    t11 = cn(className);
    $[21] = className;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ReasoningTrigger className="text-zinc-400 hover:text-zinc-200" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== hasReasoningText || $[25] !== text) {
    t13 = hasReasoningText ? <ReasoningContent data-reasoning-content="true" className={`max-h-64 overflow-y-auto pr-1 text-muted-foreground ${REASONING_TEXT_CLASS}`}>{text}</ReasoningContent> : null;
    $[24] = hasReasoningText;
    $[25] = text;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== isStreaming || $[28] !== open || $[29] !== resolvedDefaultOpen || $[30] !== t11 || $[31] !== t13 || $[32] !== title) {
    t14 = <motion.div ref={containerRef} initial={t8} animate={t9} transition={t10} className="w-full text-zinc-200"><Reasoning className={t11} defaultOpen={resolvedDefaultOpen} open={open} onOpenChange={setOpen} isStreaming={isStreaming} title={title}>{t12}{t13}</Reasoning></motion.div>;
    $[27] = isStreaming;
    $[28] = open;
    $[29] = resolvedDefaultOpen;
    $[30] = t11;
    $[31] = t13;
    $[32] = title;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}